import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { RootState } from '@/store'

import {
  addToCart,
  addUnlockedTicket,
  cartNotRestrictedQtySelector,
  cartRestrictedQtySelector,
  cartTotalSelector,
  orderItemsSelector,
  removeFromCart,
  reset,
  setCurrentStep,
  setOrderId,
  setOrderNumber,
  setPaymentError,
  setPaymentMethod,
  setPrinterStatus,
  setQty,
  setCompanyData,
  setCheckoutStartedAt,
  type CartItem,
  type CartItemId,
  type StepName
} from '../store/checkoutSlice'
import type { CompanyData } from '@/lib/api.types'
import type { PrinterState } from '@/lib/printerState'

const useCheckout = () => {
  const state = useSelector((state: RootState) => state.checkout)
  const dispatch = useDispatch()

  const setCurrentStepFn = useCallback((payload: StepName) => dispatch(setCurrentStep(payload)), [dispatch])
  const resetFn = useCallback(() => dispatch(reset()), [dispatch])
  const setPaymentMethodFn = useCallback((payload: string) => dispatch(setPaymentMethod(payload)), [dispatch])
  const setPaymentErrorFn = useCallback((payload?: string) => dispatch(setPaymentError(payload)), [dispatch])
  const addToCartFn = useCallback((payload: CartItem) => dispatch(addToCart(payload)), [dispatch])
  const removeFromCartFn = useCallback((payload: { id: CartItemId; all?: boolean }) => dispatch(removeFromCart(payload)), [dispatch])
  const setQtyFn = useCallback((payload: { id: CartItemId; qty: number }) => dispatch(setQty(payload)), [dispatch])
  const setCompanyDataFn = useCallback((payload: CompanyData | null) => dispatch(setCompanyData(payload)), [dispatch])
  const setOrderIdFn = useCallback((payload: number | string) => dispatch(setOrderId(payload)), [dispatch])
  const setOrderNumberFn = useCallback((payload: number | undefined) => dispatch(setOrderNumber(payload)), [dispatch])
  const setPrinterStatusFn = useCallback((payload: PrinterState) => dispatch(setPrinterStatus(payload)), [dispatch])
  const addUnlockedTicketFn = useCallback((payload: number) => dispatch(addUnlockedTicket(payload)), [dispatch])
  const setCheckoutStartedAtFn = useCallback((payload: number | null) => dispatch(setCheckoutStartedAt(payload)), [dispatch])

  return {
    ...state,
    cartTotal: cartTotalSelector(state),
    cartNotRestrictedQty: cartNotRestrictedQtySelector(state),
    cartRestrictedQty: cartRestrictedQtySelector(state),
    orderItems: orderItemsSelector(state),
    toOrderItems: () => orderItemsSelector(state),
    setCurrentStep: setCurrentStepFn,
    reset: resetFn,
    setPaymentMethod: setPaymentMethodFn,
    setPaymentError: setPaymentErrorFn,
    addToCart: addToCartFn,
    removeFromCart: removeFromCartFn,
    setQty: setQtyFn,
    setCompanyData: setCompanyDataFn,
    setOrderId: setOrderIdFn,
    setOrderNumber: setOrderNumberFn,
    setPrinterStatus: setPrinterStatusFn,
    addUnlockedTicket: addUnlockedTicketFn,
    setCheckoutStartedAt: setCheckoutStartedAtFn
  }
}

export default useCheckout
